import type { EngineService } from "../../platform";
import type { IntelligenceConfig } from "./types";

interface ResolvedIntelligenceSession {
    engine: EngineService;
    sessionId: string;
}

/**
 * Lazily ensures an Engine session exists for the current document.
 *
 * This resolver does NOT call `session/sync` — the canonical sync path lives
 * in `plugin-execution-store.ts` (`syncTabSessionIfNeeded`), which guards
 * against loading tabs, stale content, and tab-session mapping drift.
 * Intelligence extensions rely on that mechanism to keep the engine session
 * up-to-date, and simply obtain the session ID here for their RPC calls.
 */
export function createIntelligenceSessionResolver(config: IntelligenceConfig) {
    return async function resolveSession(): Promise<ResolvedIntelligenceSession | null> {
        const engine = config.getEngine();
        if (!engine) return null;

        let sessionId = config.getSessionId();
        if (!sessionId && config.ensureSession) {
            sessionId = await config.ensureSession().catch(() => null);
        }
        if (!sessionId) return null;

        return { engine, sessionId };
    };
}